/**
 * The library read — ADR-033 phase 3, the app half (the I/O).
 *
 * Reads World's three documents and hands them to the kernel's rules
 * (`kernel/session/library.ts`). Nothing here decides; it fetches, names
 * every failure, and composes:
 *
 *   - prod: `<base>/animations/<family>/manifest.json` + `sets.json` for each
 *     family World knows (in World's `KNOWN_FAMILIES` order — the match
 *     order), and `<base>/avatars/prebuilt/manifest.json` (the roster).
 *     Same origin on prod (`/harry/` and `/world/` share poqpoq.com), so no
 *     CORS and no token; `base` is a parameter for a dev server.
 *   - bake (F5): a local bake folder's `manifest.json` (+ `sets.json` when
 *     present) — the pool the author pins from, badged against prod. The
 *     folder arrives as thunks over its files so a directory handle, dropped
 *     files and a test's strings all feed the same seam.
 *
 * World's failure semantics are kept (prebuiltManifest.ts): a non-2xx is a
 * failure, and a 2xx that is not JSON is a failure said as one — an index
 * fallback or a CDN bleed is never laundered into an empty library. A failed
 * read is REPORTED in `errors`, never thrown past the caller: the panel must
 * still open with prod unreachable and say why each badge is uncertain.
 *
 * Depends on: kernel (library). Depended on by: the package panel (phase 5),
 * tests.
 */
import {
  badgeLibrary,
  detectLibraryFamily,
  inheritedBindings,
  parseFamilyManifest,
  parseRoster,
  parseSetsDoc,
  type BadgedLibrary,
  type FamilyDetection,
  type FamilyManifest,
  type InheritedBinding,
  type Roster,
  type SetsDoc,
} from '@harryhausen/kernel';

/** World's app root on prod; `/harry/` sits beside it on the same origin. */
export const WORLD_BASE = '/world';
/** World's `KNOWN_FAMILIES` (AnimationFamilyManifest.ts) — the ORDER is the match order. Add a line per bake. */
export const WORLD_KNOWN_FAMILIES: readonly string[] = ['tripo-2026-handed'];

export type FetchLike = (url: string) => Promise<{ ok: boolean; status: number; text(): Promise<string> }>;

export function worldLibraryUrls(base: string, familyId: string): { manifest: string; sets: string } {
  return { manifest: `${base}/animations/${familyId}/manifest.json`, sets: `${base}/animations/${familyId}/sets.json` };
}
export function worldRosterUrl(base: string): string {
  return `${base}/avatars/prebuilt/manifest.json`;
}

/** World's reader semantics: non-2xx fails; a 2xx that is not JSON fails, said as one. */
export async function fetchJsonStrict(fetchFn: FetchLike, url: string): Promise<unknown> {
  let res: Awaited<ReturnType<FetchLike>>;
  let text: string;
  try {
    // A transport failure ("Failed to fetch", offline, DNS) names no URL of
    // its own; three documents load together, so it must carry ours (Codex P2, #149).
    res = await fetchFn(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    text = await res.text();
  } catch (e) {
    throw new Error(`${url}: ${e instanceof Error ? e.message : String(e)}`);
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error(`${url}: the server answered with something that is not JSON`);
  }
}

/** A bake folder as thunks: `manifest` required, `sets` when the folder has one, `clipBytes` when its clip GLBs came along. */
export interface BakeFolder {
  readonly name: string;
  readonly manifest: () => Promise<string>;
  readonly sets?: () => Promise<string>;
  /** `<group>/<clipId>.glb` from the folder, or null when the folder has no such file (phase 4). */
  readonly clipBytes?: (group: string, clipId: string) => Promise<ArrayBuffer | null>;
}

/** What a folder file looks like to this seam: a File, a handle's file, or a test's stand-in. */
export interface FolderFileLike {
  readonly name: string;
  /** The path inside the folder (`<group>/<name>` from a handle walk); absent = `webkitRelativePath` on a native File, else the root. */
  readonly path?: string;
  /** A native File from a `webkitdirectory` input carries its folder path here (Codex P2, #151). */
  readonly webkitRelativePath?: string;
  text(): Promise<string>;
  arrayBuffer?(): Promise<ArrayBuffer>;
}

/** The path a folder file answers to: an explicit `path`, else a native File's `webkitRelativePath`, else its name. */
function folderPathOf(f: FolderFileLike): string {
  if (f.path !== undefined && f.path !== '') return f.path;
  if (f.webkitRelativePath !== undefined && f.webkitRelativePath !== '') return f.webkitRelativePath;
  return f.name;
}

/**
 * Build a BakeFolder from anything file-like — File objects, handle walks,
 * test strings. `manifest.json` and `sets.json` are read at the ROOT (a
 * folder picked one level up, or a nested bake, is not the bake); the clip
 * GLBs are indexed by `<group>/<clipId>.glb` so a preview can play a staged
 * clip prod does not carry yet.
 *
 * The root is the PICKED folder, named by `name`: both pickers prefix every
 * path with it (a handle walk by construction, a `webkitdirectory` input by
 * the platform), so a path's first segment IS the root when it equals
 * `name`, and a path without that prefix (a test's bare `manifest.json`, a
 * dropped file) already sits at the root. The root is never inferred from
 * the shallowest file (Codex P2, #151 round two): a picked folder holding
 * two sibling bakes and nothing shallower would have had BOTH manifests at
 * the minimum depth, one chosen by iteration order, the clip map merged
 * from both — a preview playing another version's bytes.
 */
export function bakeFolderFromFiles(name: string, files: Iterable<FolderFileLike>): BakeFolder | null {
  let manifest: (() => Promise<string>) | undefined;
  let sets: (() => Promise<string>) | undefined;
  const clips = new Map<string, FolderFileLike>();
  for (const f of files) {
    const segments = folderPathOf(f).split('/').filter((s) => s !== '');
    const rel = (segments.length > 1 && segments[0] === name ? segments.slice(1) : segments).join('/');
    if (rel === 'manifest.json') manifest = () => f.text();
    else if (rel === 'sets.json') sets = () => f.text();
    else if (/^[^/]+\/[^/]+\.glb$/.test(rel) && f.arrayBuffer !== undefined) clips.set(rel, f);
  }
  if (manifest === undefined) return null;
  return {
    name,
    manifest,
    ...(sets !== undefined ? { sets } : {}),
    ...(clips.size > 0
      ? {
          clipBytes: async (group: string, clipId: string): Promise<ArrayBuffer | null> => {
            const f = clips.get(`${group}/${clipId}.glb`);
            return f?.arrayBuffer === undefined ? null : f.arrayBuffer();
          },
        }
      : {}),
  };
}

export interface ProdFamily {
  readonly id: string;
  readonly manifest: FamilyManifest | null;
  readonly sets: SetsDoc | null;
}

export interface LibraryRead {
  /** Prod families in World's match order; a family whose manifest failed has `manifest: null` and a line in `errors`. */
  readonly prod: readonly ProdFamily[];
  readonly roster: Roster | null;
  /** The bake folder read, when one was given and parsed; `clipBytes` rides along for previews (phase 4). */
  readonly bake: { readonly name: string; readonly manifest: FamilyManifest; readonly sets: SetsDoc | null; readonly clipBytes?: BakeFolder['clipBytes'] } | null;
  /** Every read that failed, named — the panel shows these beside the badges they make uncertain. */
  readonly errors: readonly string[];
}

export interface ReadLibraryInputs {
  readonly fetchFn: FetchLike;
  readonly base?: string;
  readonly families?: readonly string[];
  readonly bake?: BakeFolder;
}

const said = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export async function readLibrary(inputs: ReadLibraryInputs): Promise<LibraryRead> {
  const base = inputs.base ?? WORLD_BASE;
  const families = inputs.families ?? WORLD_KNOWN_FAMILIES;
  const errors: string[] = [];

  const prod = await Promise.all(
    families.map(async (id): Promise<ProdFamily> => {
      const urls = worldLibraryUrls(base, id);
      let manifest: FamilyManifest | null = null;
      let sets: SetsDoc | null = null;
      try {
        manifest = parseFamilyManifest(await fetchJsonStrict(inputs.fetchFn, urls.manifest), urls.manifest);
        if (manifest.family.id !== id) {
          errors.push(`${urls.manifest}: declares family "${manifest.family.id}", asked for "${id}"`);
          manifest = null;
        }
      } catch (e) {
        errors.push(said(e));
      }
      try {
        sets = parseSetsDoc(await fetchJsonStrict(inputs.fetchFn, urls.sets), urls.sets);
        // A sets document for another family (a partial deploy, a CDN
        // mix-up) must not be resolved for this rig (Codex P1, #149).
        if (sets.family !== id) {
          errors.push(`${urls.sets}: declares family "${sets.family}", asked for "${id}"`);
          sets = null;
        }
      } catch (e) {
        errors.push(said(e));
      }
      return { id, manifest, sets };
    }),
  );

  let roster: Roster | null = null;
  try {
    roster = parseRoster(await fetchJsonStrict(inputs.fetchFn, worldRosterUrl(base)), worldRosterUrl(base));
  } catch (e) {
    errors.push(said(e));
  }

  let bake: LibraryRead['bake'] = null;
  if (inputs.bake !== undefined) {
    try {
      const manifest = parseFamilyManifest(JSON.parse(await inputs.bake.manifest()) as unknown, `${inputs.bake.name}/manifest.json`);
      let sets: SetsDoc | null = null;
      if (inputs.bake.sets !== undefined) {
        try {
          sets = parseSetsDoc(JSON.parse(await inputs.bake.sets()) as unknown, `${inputs.bake.name}/sets.json`);
          if (sets.family !== manifest.family.id) {
            errors.push(`${inputs.bake.name}/sets.json: declares family "${sets.family}", the folder's manifest is "${manifest.family.id}"`);
            sets = null;
          }
        } catch (e) {
          errors.push(said(e));
        }
      }
      bake = { name: inputs.bake.name, manifest, sets, ...(inputs.bake.clipBytes !== undefined ? { clipBytes: inputs.bake.clipBytes } : {}) };
    } catch (e) {
      errors.push(said(e));
    }
  }

  return { prod, roster, bake, errors };
}

export interface LibraryForRig {
  /** World's detection over the families whose prod manifest was read (the bake's family counts when prod failed). */
  readonly detection: FamilyDetection;
  /** The family's pool badged against prod — the bake when given (F5), else prod badged against itself. */
  readonly badged: BadgedLibrary | null;
  /** The sets in force for this family: the bake's `sets.json` when it has one, else prod's. */
  readonly sets: SetsDoc | null;
  /** What this avatar inherits at its entity path: those sets, then the manifest's contractKeys for keys no binding reaches. */
  readonly inherited: readonly InheritedBinding[];
  readonly reasons: readonly string[];
}

/**
 * The read, asked about ONE rig: which family, from which pool, inheriting
 * what at this entity path. Pure over the read. A bake for a family other
 * than the rig's is a mismatch said in `reasons`, not a silent fall-through.
 */
export function libraryForRig(read: LibraryRead, boneNames: readonly string[], entityPath: string): LibraryForRig {
  const reasons: string[] = [];
  // Candidates in World's order. A family whose prod manifest failed keeps
  // its POSITION when the bake supplies it (first match wins, so order is
  // part of the rule — Codex P2, #149); a bake family World does not list
  // goes last.
  const bakeFamily = read.bake?.manifest.family ?? null;
  const candidates: { id: string; bodySignature: readonly string[] }[] = [];
  for (const p of read.prod) {
    if (p.manifest !== null) candidates.push({ id: p.id, bodySignature: p.manifest.family.bodySignature });
    else if (bakeFamily !== null && bakeFamily.id === p.id) candidates.push({ id: p.id, bodySignature: bakeFamily.bodySignature });
  }
  if (bakeFamily !== null && !candidates.some((c) => c.id === bakeFamily.id)) {
    candidates.push({ id: bakeFamily.id, bodySignature: bakeFamily.bodySignature });
  }
  const detection = detectLibraryFamily(boneNames, candidates);
  if (detection.family === null) {
    reasons.push(
      candidates.length === 0
        ? 'no family manifest could be read — World cannot say what animates this rig'
        : `the rig matches no World animation family — ${detection.misses.map((m) => `${m.id}: missing ${m.missing.length}/${m.required} (${m.missing.slice(0, 4).join(', ')}${m.missing.length > 4 ? ', …' : ''})`).join('; ')}`,
    );
    return { detection, badged: null, sets: null, inherited: [], reasons };
  }
  const familyId = detection.family.id;
  const prodFamily = read.prod.find((p) => p.id === familyId) ?? null;
  const prodManifest = prodFamily?.manifest ?? null;
  let badged: BadgedLibrary | null = null;
  if (read.bake !== null && read.bake.manifest.family.id === familyId) {
    badged = badgeLibrary(read.bake.manifest, prodManifest, prodManifest === null ? `prod manifest for ${familyId} was not read` : null);
  } else {
    if (read.bake !== null) reasons.push(`the bake "${read.bake.name}" is family "${read.bake.manifest.family.id}", the rig is "${familyId}" — pinning from prod's pool instead`);
    if (prodManifest !== null) badged = badgeLibrary(prodManifest, prodManifest);
  }
  const sets = (read.bake !== null && read.bake.manifest.family.id === familyId ? read.bake.sets : null) ?? prodFamily?.sets ?? null;
  // World's fallback when no binding reaches a key: the manifest's contractKeys — the pool's own manifest, chosen by the SAME family match as the pool and the sets (never by version equality — Codex P1, #149 round two).
  const poolManifest = read.bake !== null && read.bake.manifest.family.id === familyId ? read.bake.manifest : prodManifest;
  const contractKeys = poolManifest?.family.contractKeys ?? {};
  if (sets === null) reasons.push(`no sets document for ${familyId} — only the manifest's contractKeys are inherited`);
  return { detection, badged, sets, inherited: inheritedBindings(sets, entityPath, contractKeys), reasons };
}
